// ============================================================================
// BORG Analytics Client – Type Definitions & Implementation
// ============================================================================

declare global {
  interface Window {
    dataLayer: BorgEvent[];
  }
}

/**
 * Közös mezők minden dL eseményben
 */
export interface BorgBaseEvent {
  event: string;
  channel: 'web' | 'app';
  clientId?: string;
  virtualName: string;
  location: string;
  parentUrl?: string;
}

/**
 * Virtual Pageview – közös Login, Registration
 */
export interface BorgVirtualPageView extends BorgBaseEvent {
  event: 'virtualPV';
}

/**
 * Login event (OpenAM / yui-web)
 * Spec: DAO-BORG Login - OpenAM
 */
export interface BorgLoginEvent extends BorgBaseEvent {
  event: 'login';
  method: 'email' | 'sms' | 'msisdn' | 'devicecode';
  action: 'First verification' | 'Sms verification' | 'Second verification' | 'login_failed';
  result: 'true' | 'false';
  reason: string;
  externalId?: string;
  accountId?: string;
}

/**
 * Registration event (ALM / alm-web)
 * Spec: Registration - ALM domain
 */
export interface BorgRegistrationEvent extends BorgBaseEvent {
  event: 'registration';
  action:
    | 'EmailVerificationCodeSent'
    | 'EmailVerificationStatus'
    | 'SMSVerificationBypass'
    | 'SMSVerification'
    | 'SMSVerificationCodeSent'
    | 'ProcessFinished'
    | 'TechnicalError'
    | 'SMSVerificationStatus';
  result: 'true' | 'false';
  reason?: string;
}

/**
 * Union type az összes BORG eseményre
 */
export type BorgEvent = BorgVirtualPageView | BorgLoginEvent | BorgRegistrationEvent;

/**
 * Auto-filled fields that the client populates if not explicitly provided.
 */
type AutoFields = 'event' | 'location' | 'clientId' | 'parentUrl';

/**
 * Makes auto-filled base fields optional in method params.
 * Callers must provide `channel` explicitly.
 */
type WithDefaults<T extends BorgBaseEvent> = Omit<T, AutoFields> &
  Partial<Pick<T, 'location' | 'clientId' | 'parentUrl'>>;

// ============================================================================
// Implementation
// ============================================================================

/**
 * BORG Analytics Client
 *
 * Felelősség: BORG-spec szerinti események küldése a GTM dataLayer-be.
 * Használat: singleton pattern, közvetlenül importálható minden komponensből.
 */
class BorgAnalyticsClient {
  /**
   * Virtual PageView küldése
   */
  sendVirtualPageView(params: WithDefaults<BorgVirtualPageView>): void {
    this.pushToDataLayer({
      event: 'virtualPV',
      ...this.getDefaults(),
      ...params,
    });
  }

  /**
   * Login esemény küldése (yui-web)
   */
  sendLoginEvent(params: WithDefaults<BorgLoginEvent>): void {
    this.pushToDataLayer({
      event: 'login',
      ...this.getDefaults(),
      ...params,
    });
  }

  /**
   * Registration esemény küldése (alm-web)
   */
  sendRegistrationEvent(params: WithDefaults<BorgRegistrationEvent>): void {
    this.pushToDataLayer({
      event: 'registration',
      ...this.getDefaults(),
      ...params,
    });
  }

  private getDefaults(): Omit<BorgBaseEvent, 'event' | 'virtualName' | 'channel'> {
    return {
      clientId: getClientId(),
      location: window.location.href,
      parentUrl: getParentUrl(),
    };
  }

  /**
   * Általános dataLayer push
   */
  private pushToDataLayer(event: BorgEvent): void {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(event);

      // @ts-ignore – process.env.NODE_ENV is injected by the bundler at build time
      if (process.env.NODE_ENV === 'development') {
        console.log('[BORG Analytics]', event);
      }
    } catch (error) {
      console.error('[BORG Analytics] Error pushing to dataLayer:', error);
    }
  }
}

/**
 * Singleton instance export
 */
export const borgAnalytics = new BorgAnalyticsClient();

// ============================================================================
// Helper functions
// ============================================================================

/**
 * Helper: channel meghatározása (web vagy app)
 */
export const getChannel = (isWebView: boolean): 'web' | 'app' => {
  return isWebView ? 'app' : 'web';
};

/**
 * Helper: clientId kinyerése URL-ből (OpenAM)
 * ALM-ben mindig 'undefined' string-ként küldendő.
 */
export const getClientId = (): string | undefined => {
  try {
    const url = new URL(window.location.href);
    const goto = url.searchParams.get('goto');

    if (goto) {
      const gotoUrl = new URL(goto);
      const clientId = gotoUrl.searchParams.get('client_id');
      if (clientId) return clientId;
    }

    return url.searchParams.get('client_id') || undefined;
  } catch {
    return undefined;
  }
};

/**
 * Helper: parentUrl kinyerése (OpenAM/ALM)
 */
export const getParentUrl = (): string | undefined => {
  try {
    const url = new URL(window.location.href);
    return url.searchParams.get('parentUrl') || url.searchParams.get('goto') || undefined;
  } catch {
    return undefined;
  }
};
